import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getCases, deleteCase, reorderCases } from '../../api/cases';
import CaseCategoryManager from '../../components/admin/CaseCategoryManager';

const CaseManagement = () => {
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCases();
    }, []);

    const fetchCases = async () => {
        try {
            const data = await getCases();
            setCases(data);
        } catch (error) {
            console.error(error);
            alert('사례 목록 불러오기 실패');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteCase(id);
            alert('삭제되었습니다.');
            fetchCases();
        } catch (error) {
            console.error(error);
            alert('삭제 실패');
        }
    };

    // Reorder Modal Functions
    const openReorderModal = () => {
        setReorderList([...cases]);
        setIsReorderModalOpen(true);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === index) return;

        const newList = [...reorderList];
        const [draggedItem] = newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);
        setReorderList(newList);
        setDraggedIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const handleSaveReorder = async () => {
        try {
            const ids = reorderList.map(item => item.id);
            await reorderCases(ids);
            alert('순서가 저장되었습니다.');
            setIsReorderModalOpen(false);
            fetchCases();
        } catch (error) {
            console.error(error);
            alert('순서 저장 실패');
        }
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">사례 관리</h2>
                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <button onClick={() => setIsCategoryModalOpen(true)} className="flex-1 md:flex-none px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-bold border border-gray-200 rounded-xl shadow-sm transition-colors text-sm">
                        ⚙️ 카테고리 관리
                    </button>
                    <button className="flex-1 md:flex-none px-5 py-2.5 bg-gray-800 hover:bg-gray-900 text-white font-bold rounded-xl shadow-sm transition-colors text-sm" onClick={() => navigate('/admin/cases/new')}>
                        + 사례 추가
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex justify-end p-4 border-b border-gray-100 bg-gray-50/50">
                    <button
                        onClick={openReorderModal}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold text-sm rounded-lg transition-colors shadow-sm"
                    >
                        ↕ 순서 변경
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="px-5 py-4 font-bold w-16">순서</th>
                                <th className="px-5 py-4 font-bold w-32">카테고리</th>
                                <th className="px-5 py-4 font-bold">제목</th>
                                <th className="px-5 py-4 font-bold w-32 text-center">작업</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-gray-700">
                            {loading ? (
                                <tr><td colSpan="4" className="p-10 text-center text-gray-400 font-bold animate-pulse">데이터 로딩 중...</td></tr>
                            ) : cases.length === 0 ? (
                                <tr><td colSpan="4" className="p-10 text-center text-gray-400 font-bold bg-gray-50">등록된 사례가 없습니다.</td></tr>
                            ) : cases.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                                    <td className="px-5 py-3.5 font-mono text-gray-400 text-xs">{item.order}</td>
                                    <td className="px-5 py-3.5 font-bold"><span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">{item.category?.label || '-'}</span></td>
                                    <td className="px-5 py-3.5 font-semibold text-gray-900">{item.title}</td>
                                    <td className="px-5 py-3.5 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-colors"
                                                onClick={() => navigate(`/admin/cases/${item.id}`)}
                                            >
                                                수정
                                            </button>
                                            <button
                                                className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Category Manager Modal */}
            <CaseCategoryManager
                isOpen={isCategoryModalOpen}
                onClose={() => {
                    setIsCategoryModalOpen(false);
                    fetchCases();
                }}
            />

            {/* Reorder Modal */}
            {isReorderModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden p-6">
                        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100">
                            <h3 className="text-xl font-bold text-gray-800 m-0">사례 순서 변경</h3>
                            <button onClick={() => setIsReorderModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none bg-transparent outline-none">&times;</button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100 font-medium">
                            드래그하여 순서를 변경하세요. 저장 시 순서가 최상단부터 1위로 자동 재정렬됩니다.
                        </p>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                            {reorderList.map((item, index) => (
                                <div
                                    key={item.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className={`flex items-center gap-4 p-3 rounded-xl border cursor-grab transition-colors active:cursor-grabbing ${draggedIndex === index ? 'bg-primary-50 border-primary-200 shadow-md scale-[1.02]' : 'bg-white border-gray-200 shadow-sm hover:border-gray-300'}`}
                                >
                                    <span className="text-gray-400 text-lg cursor-grab">☰</span>
                                    <span className="w-8 h-8 rounded-full bg-gray-800 text-white flex items-center justify-center text-xs font-bold shadow-sm shrink-0">
                                        {index + 1}
                                    </span>
                                    <div className="flex-1 overflow-hidden">
                                        <div className="font-bold text-gray-800 truncate">{item.title}</div>
                                        <div className="text-xs text-gray-500 font-medium truncate">{item.category?.label || '-'}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 mt-6 pt-4 border-t border-gray-100 shrink-0">
                            <button
                                onClick={() => setIsReorderModalOpen(false)}
                                className="flex-1 py-2.5 bg-white text-gray-700 font-bold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleSaveReorder}
                                className="flex-1 py-2.5 bg-gray-800 text-white font-bold rounded-xl hover:bg-gray-900 transition-colors shadow-sm"
                            >
                                순서 저장 적용
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default CaseManagement;
